import { ApplicationController } from "../../support/application-controller";

/**
 * Controller for the main duplicate mappings admin page.
 * Handles the auto-resolve form with async preview and accept flow.
 */
export default class extends ApplicationController {
    static targets = [
        "autoResolveForm",
        "dryRunCheckbox",
        "autoResolveButton",
        "autoResolveButtonText",
        "acceptContainer",
        "acceptButton",
        "acceptButtonText",
        "errorMessage",
        "previewResults"
    ];

    connect() {
        this.autoResolveUrl = this.data.get("autoResolveUrl");
        this._updateButtonState();
    }

    toggleDryRun(event) {
        this._updateButtonState();
        // Reset preview state when toggling
        this._resetPreview();
    }

    /**
     * Handle form submission - intercept for async preview when dry_run is checked
     */
    handleSubmit(event) {
        const isDryRun = this.hasDryRunCheckboxTarget && this.dryRunCheckboxTarget.checked;

        // Only intercept for dry-run mode
        if (!isDryRun) {
            return; // Let the form submit normally
        }

        event.preventDefault();

        // Show loading state
        this._setLoading(true);
        this._hideError();

        const self = this;

        fetch(this.autoResolveUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this._getCSRFToken()
            },
            body: JSON.stringify({
                dry_run: "true"
            })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                if (!response.ok || data.success === false) {
                    throw new Error(data.error || "Failed to preview auto-resolution");
                }
                return data;
            });
        })
        .then(function(data) {
            // Show preview results and accept button
            self._showPreview(data);
            self._setLoading(false);
        })
        .catch(function(error) {
            console.error("Preview auto-resolution error:", error);
            self._showError(error.message || "An error occurred while previewing the auto-resolution");
            self._setLoading(false);
        });
    }

    /**
     * Accept the previewed resolution and execute it (non-dry-run)
     */
    acceptResolution(event) {
        event.preventDefault();

        // Show loading state on Accept button
        this._setAcceptLoading(true);
        this._hideError();

        const self = this;

        fetch(this.autoResolveUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this._getCSRFToken()
            },
            body: JSON.stringify({
                // No dry_run flag = actual execution
            })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                if (!response.ok || data.success === false) {
                    throw new Error(data.error || "Failed to execute auto-resolution");
                }
                return data;
            });
        })
        .then(function(data) {
            // Resolution successful - reload the page to show updated list
            window.location.reload();
        })
        .catch(function(error) {
            console.error("Execute auto-resolution error:", error);
            self._showError(error.message || "An error occurred while executing the auto-resolution");
            self._setAcceptLoading(false);
        });
    }

    /**
     * Show the preview results and accept button
     */
    _showPreview(data) {
        const autoResolvedCount = (data.auto_resolved || []).length;

        // Update preview results text if target exists
        if (this.hasPreviewResultsTarget) {
            this.previewResultsTarget.textContent = `Preview complete: ${autoResolvedCount} duplicate(s) will be resolved`;
            this.previewResultsTarget.classList.remove("duplicate-mappings__preview-results--hidden");
        }

        // Show the Accept button
        if (this.hasAcceptContainerTarget) {
            this.acceptContainerTarget.classList.remove("duplicate-mappings__accept-action--hidden");
        }

        // Update the preview button to indicate preview is done
        if (this.hasAutoResolveButtonTextTarget) {
            this.autoResolveButtonTextTarget.textContent = "Preview Complete";
        }
        if (this.hasAutoResolveButtonTarget) {
            this.autoResolveButtonTarget.disabled = true;
        }
    }

    /**
     * Reset the preview state
     */
    _resetPreview() {
        // Hide preview results
        if (this.hasPreviewResultsTarget) {
            this.previewResultsTarget.classList.add("duplicate-mappings__preview-results--hidden");
            this.previewResultsTarget.textContent = "";
        }

        // Hide the Accept button
        if (this.hasAcceptContainerTarget) {
            this.acceptContainerTarget.classList.add("duplicate-mappings__accept-action--hidden");
        }

        // Re-enable preview button
        if (this.hasAutoResolveButtonTarget) {
            this.autoResolveButtonTarget.disabled = false;
        }

        // Hide any error messages
        this._hideError();
    }

    _updateButtonState() {
        if (!this.hasDryRunCheckboxTarget || !this.hasAutoResolveButtonTextTarget) {
            return;
        }

        const isDryRun = this.dryRunCheckboxTarget.checked;

        if (isDryRun) {
            this.autoResolveButtonTextTarget.textContent = "Preview Auto-Resolution";
            if (this.hasAutoResolveButtonTarget) {
                this.autoResolveButtonTarget.classList.remove("stop");
            }
        } else {
            this.autoResolveButtonTextTarget.textContent = "Execute Auto-Resolution";
            if (this.hasAutoResolveButtonTarget) {
                this.autoResolveButtonTarget.classList.add("stop");
            }
        }
    }

    _setLoading(isLoading) {
        if (!this.hasAutoResolveButtonTarget) return;

        if (isLoading) {
            this.autoResolveButtonTarget.disabled = true;
            if (this.hasAutoResolveButtonTextTarget) {
                this._originalButtonText = this.autoResolveButtonTextTarget.textContent;
                this.autoResolveButtonTextTarget.textContent = "Loading...";
            }
        } else {
            this.autoResolveButtonTarget.disabled = false;
            if (this.hasAutoResolveButtonTextTarget && this._originalButtonText) {
                this.autoResolveButtonTextTarget.textContent = this._originalButtonText;
            }
        }
    }

    _setAcceptLoading(isLoading) {
        if (!this.hasAcceptButtonTarget) return;

        if (isLoading) {
            this.acceptButtonTarget.disabled = true;
            if (this.hasAcceptButtonTextTarget) {
                this._originalAcceptButtonText = this.acceptButtonTextTarget.textContent;
                this.acceptButtonTextTarget.textContent = "Executing...";
            }
        } else {
            this.acceptButtonTarget.disabled = false;
            if (this.hasAcceptButtonTextTarget && this._originalAcceptButtonText) {
                this.acceptButtonTextTarget.textContent = this._originalAcceptButtonText;
            }
        }
    }

    _showError(message) {
        if (!this.hasErrorMessageTarget) return;

        this.errorMessageTarget.textContent = message;
        this.errorMessageTarget.classList.remove("duplicate-mappings__error-message--hidden");
    }

    _hideError() {
        if (!this.hasErrorMessageTarget) return;

        this.errorMessageTarget.classList.add("duplicate-mappings__error-message--hidden");
        this.errorMessageTarget.textContent = "";
    }

    _getCSRFToken() {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.content : "";
    }
}
